import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { DatePicker, Row, Col, DatePickerProps } from 'antd';
import Page from '../../components/Page/Page';
import styles from './DashboardPage.module.less';
import Title from '../../components/Page/Title';
import DashboardChartWidget from '../../components/DashboardWidget/DashboardChartWidget';
import DashboardNumberTileWidget from '../../components/DashboardWidget/DashboardNumberTileWidget';
import CheckmarkIcon from '../../assets/img/checkMark.svg?react';
import CancelIcon from '../../assets/img/cancelIcon.svg?react';
import AverageOrdersIcon from '../../assets/img/mapPinsIcon.svg?react';
import {
  getMeteringCount,
  MeteringCountParams,
  getMeteringTimeseries,
  getMeteringHistogram,
  MeteringTimeseriesDataParams,
  MeteringHistogramDataParams
} from '../../api/metering';
import DashboardColumnChartWidget from '../../components/DashboardWidget/DashboardColumnChartWidget';
import DashboardAreaChartWidget from '../../components/DashboardWidget/DashboardAreaChartWidget';
import { MeteringTimeseriesMap } from '../../api/models/metering';
import { meteringTimeseriesItemNameTransform } from '../../api/transformers/metering';
import { getTimeStringFromSeconds } from '../../utils/dates';
import { DASHBOARD_DATA_RANGE_DAYS } from '../../utils/constants';
import useAnalytika from '../../utils/analytika/reporterAnalytika';
import { dashboardPageOpen } from '../../utils/analytika/events';
import { RangePickerValue } from '../../types/RangePickerValue';
import { colorError, colorPrimary, colorWarning } from '../../styles/theme';

const showHistogram = false;

export const isDateDisabled: DatePickerProps['disabledDate'] = current =>
  Boolean(
    (current && current.startOf('day').toDate() >= new Date()) ||
      (current && current.startOf('day').toDate() < dayjs().subtract(DASHBOARD_DATA_RANGE_DAYS, 'd').toDate())
  );

export const getAverageFromArr = (arr: { value: number }[]) => {
  const filtered = arr?.length ? arr.filter(item => item.value > 0) : [];
  return (filtered.length && filtered.reduce((prev, cur) => prev + cur.value, 0) / filtered.length) || 0;
};

const customAreaStyleSetter = () => {
  return { fill: 'l(270) 0:#ffffff 1:#f5841f' };
};

const completedDeliveredParam = {
  attribute: 'deliverystatus',
  value: 'DELIVERY_ENDED'
};

const cancelledByMerchantParam = {
  attribute: 'iscanceledbymerchant',
  value: true
};

const cancelledByCaptainParam = {
  attribute: 'iscanceledbycaptain',
  value: true
};

const cancelledByCareParam = [
  {
    attribute: 'deliverystatus',
    value: 'CANCELED'
  },
  {
    attribute: 'iscanceledbycaptain',
    value: false
  },
  {
    attribute: 'iscanceledbymerchant',
    value: false
  }
];

const cancelledCaptainNotFoundParam = {
  attribute: 'deliverystatus',
  value: 'CAPTAIN_NOT_FOUND'
};

const hasDropoffParam = {
  attribute: 'hasdropoff',
  value: true
};

const hasNoDropoffFParam = {
  attribute: 'hasdropoff',
  value: false
};

const getMeteringCountQuery = ({ queryKey }: { queryKey: [string, MeteringCountParams] }) => {
  const [, params] = queryKey;
  return getMeteringCount(params);
};

const getMeteringTimeseriesQuery = ({ queryKey }: { queryKey: [string, MeteringTimeseriesDataParams] }) => {
  const [, params] = queryKey;
  return getMeteringTimeseries(params);
};

const getMeteringHistogramQuery = ({ queryKey }: { queryKey: [string, MeteringHistogramDataParams] }) => {
  const [, params] = queryKey;
  return getMeteringHistogram(params);
};

export const timeFormatter = (daysCount: number): string => {
  return daysCount === 0 ? 'h a' : daysCount < 3 ? 'MMM D, h a' : 'MMM D';
};

export const DashboardPage = () => {
  const initialDaysNumber = 6;
  const { t } = useTranslation();

  const [daysCount, setDaysCount] = useState(initialDaysNumber);
  const [timeFormat, setTimeFormat] = useState(timeFormatter(initialDaysNumber));
  const initStart = new Date(Date.now() - daysCount * 24 * 60 * 60 * 1000);
  const initEnd = new Date();
  const [dates, setDates] = useState<RangePickerValue>([dayjs(initStart), dayjs()]);
  const [totalCompleted, setTotalCompleted] = useState(0);
  const [totalCancelled, setTotalCancelled] = useState(0);
  const [totalCompletedPercent, setTotalCompletedPercent] = useState(0);
  const [totalCancelledPercent, setTotalCancelledPercent] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [avgOrdersPerDay, setAvgOrdersPerDay] = useState(Math.round(totalOrders / (daysCount + 1)));
  const [timeFromStartedToEndedAvg, setTimeFromStartedToEndedAvg] = useState('0');
  const [timeToAssignCaptainAvg, setTimeToAssignCaptainAvg] = useState('0');
  const [timeToReachMerchantAvg, setTimeToReachMerchantAvg] = useState('0');
  const [timeCaptainWaitingAvg, setTimeCaptainWaitingAvg] = useState('0');
  const [timeBtwPickupNDropoffAvg, setTimeBtwPickupNDropoffAvg] = useState('0');
  const [distanceBtwAssignmentNPickupAvg, setDistanceBtwAssignmentNPickupAvg] = useState('0');
  const [distanceBtwPickupNDropoffAvg, setDistanceBtwPickupNDropoffAvg] = useState('0');
  const [timeWhenDropoffAvailableAvg, setTimeWhenDropoffAvailableAvg] = useState('0');
  const [timeWhenDropoffNotAvailableAvg, setTimeWhenDropoffNotAvailableAvg] = useState('0');
  const [distanceWhenDropoffAvailableAvg, setDistanceWhenDropoffAvailableAvg] = useState('0');
  const [distanceWhenDropoffNotAvailableAvg, setDistanceWhenDropoffNotAvailableAvg] = useState('0');

  const basicQueryParams = {
    datasource: 'marketplace',
    startDate: dates![0]?.startOf('day').toDate(),
    endDate: dates![1]?.endOf('day').toDate()
  };

  const {
    data: completedDeliveredNumber = 0,
    isFetching: isCompletedDeliveredFetching,
    isError: isCompletedDeliveredError
  } = useQuery(
    [
      'getCompletedDeliveredNumber',
      {
        ...basicQueryParams,
        filterBy: JSON.stringify([completedDeliveredParam])
      }
    ],
    //@ts-ignore
    getMeteringCountQuery
  );

  const {
    data: cancelledCapNotFoundNumber = 0,
    isFetching: isCancelledCapNotFoundFetching,
    isError: isCancelledCapNotFoundError
  } = useQuery(
    [
      'getCancelledCapNotFoundNumber',
      {
        ...basicQueryParams,
        filterBy: JSON.stringify([cancelledCaptainNotFoundParam])
      }
    ],
    //@ts-ignore
    getMeteringCountQuery
  );

  const {
    data: cancelledByMerchantNumber = 0,
    isFetching: isCancelledByMerchantFetching,
    isError: isCancelledByMerchantError
  } = useQuery(
    [
      'getCancelledByMerchantNumber',
      {
        ...basicQueryParams,
        filterBy: JSON.stringify([cancelledByMerchantParam])
      }
    ],
    //@ts-ignore
    getMeteringCountQuery
  );

  const {
    data: cancelledByCaptainNumber = 0,
    isFetching: isCancelledByCaptainFetching,
    isError: isCancelledByCaptainError
  } = useQuery(
    [
      'getCancelledByCaptainNumber',
      {
        ...basicQueryParams,
        filterBy: JSON.stringify([cancelledByCaptainParam])
      }
    ],
    //@ts-ignore
    getMeteringCountQuery
  );

  const {
    data: cancelledByCareNumber = 0,
    isFetching: isCancelledByCareFetching,
    isError: isCancelledByCareError
  } = useQuery(
    [
      'getCancelledByCareNumber',
      {
        ...basicQueryParams,
        filterBy: JSON.stringify(cancelledByCareParam)
      }
    ],
    //@ts-ignore
    getMeteringCountQuery
  );

  const {
    data: breakdownDeliveriesByDistancesData = [],
    isFetching: isBreakdownDeliveriesByDistancesDataFetching,
    isError: isBreakdownDeliveriesByDistancesDataError
  } = (showHistogram &&
    useQuery(
      [
        'getBreakdownDeliveriesByDistancesData',
        {
          ...basicQueryParams,
          filterBy: JSON.stringify([completedDeliveredParam]),
          attribute: 'distancetraveled'
        }
      ],
      //@ts-ignore
      getMeteringHistogramQuery
    )) || { data: [], isFetching: false, isError: false };

  const {
    data: singleAreaChartsData = {},
    isFetching: isSingleAreaChartsDataFetching,
    isError: isSingleAreaChartsDataError
  } = useQuery(
    [
      'getSingleAreaChartsData',
      {
        ...basicQueryParams,
        filterBy: JSON.stringify([completedDeliveredParam]),
        attributes: [
          'timefromupcomingtodeliveryended',
          'timefromupcomingtocaptainassigned',
          'timefromcaptainassignedtocaptainhere',
          'timefromcaptainheretodeliverystarted',
          'timefromdeliverystartedtodeliveryended',
          'distancefromcaptaintopickup',
          'distancefrompickuptodropoff'
        ].join(',')
      }
    ],
    //@ts-ignore
    getMeteringTimeseriesQuery,
    {
      onSuccess: (successData: MeteringTimeseriesMap) => {
        setTimeFromStartedToEndedAvg(
          getTimeStringFromSeconds(getAverageFromArr(successData.timefromupcomingtodeliveryended))
        );
        setTimeToAssignCaptainAvg(
          getTimeStringFromSeconds(getAverageFromArr(successData.timefromupcomingtocaptainassigned))
        );
        setTimeToReachMerchantAvg(
          getTimeStringFromSeconds(getAverageFromArr(successData.timefromcaptainassignedtocaptainhere))
        );
        setTimeCaptainWaitingAvg(
          getTimeStringFromSeconds(getAverageFromArr(successData.timefromcaptainheretodeliverystarted))
        );
        setTimeBtwPickupNDropoffAvg(
          getTimeStringFromSeconds(getAverageFromArr(successData.timefromdeliverystartedtodeliveryended))
        );
        setDistanceBtwAssignmentNPickupAvg(
          `${(getAverageFromArr(successData.distancefromcaptaintopickup) / 1000).toFixed(1)}`
        );
        setDistanceBtwPickupNDropoffAvg(
          `${(getAverageFromArr(successData.distancefrompickuptodropoff) / 1000).toFixed(1)}`
        );
      }
    }
  );

  const {
    timefromupcomingtodeliveryended: timeToCompleteDeliveryData = [],
    timefromupcomingtocaptainassigned: timeToAssignCaptainData = [],
    timefromcaptainassignedtocaptainhere: timeToReachMerchantData = [],
    timefromcaptainheretodeliverystarted: timeCaptainWaitingData = [],
    timefromdeliverystartedtodeliveryended: timeBtwPickupNDropoffData = [],
    distancefromcaptaintopickup: distanceBtwAssignmentNPickupData = [],
    distancefrompickuptodropoff: distanceBtwPickupNDropoffData = []
  } = singleAreaChartsData;

  const {
    data: deliveryTimeDistanceDropoffAvailableData = {
      distancefrompickuptodropoff: [],
      timefromupcomingtodeliveryended: []
    },
    isFetching: isDeliveryTimeDistanceDropoffAvailableDataFetching,
    isError: isDeliveryTimeDistanceDropoffAvailableDataError
  } = useQuery(
    [
      'getDeliveryTimeDistanceDropoffAvailableData',
      {
        ...basicQueryParams,
        filterBy: JSON.stringify([hasDropoffParam, completedDeliveredParam]),
        attributes: ['distancefrompickuptodropoff', 'timefromupcomingtodeliveryended'].join(',')
      }
    ],
    //@ts-ignore
    getMeteringTimeseriesQuery
  );

  const {
    //@ts-ignore
    distancefrompickuptodropoff: deliveryDistanceDropoffAvailableData = [],
    //@ts-ignore
    timefromupcomingtodeliveryended: deliveryTimeDropoffAvailableData = []
  } = deliveryTimeDistanceDropoffAvailableData;

  const {
    data: deliveryTimeDistanceDropoffNotAvailableData = {
      distancefrompickuptodropoff: [],
      timefromupcomingtodeliveryended: []
    },
    isFetching: isDeliveryTimeDistanceDropoffNotAvailableDataFetching,
    isError: isDeliveryTimeDistanceDropoffNotAvailableDataError
  } = useQuery(
    [
      'getDeliveryTimeDistanceDropoffNotAvailableData',
      {
        ...basicQueryParams,
        filterBy: JSON.stringify([hasNoDropoffFParam, completedDeliveredParam]),
        attributes: ['distancefrompickuptodropoff', 'timefromupcomingtodeliveryended'].join(',')
      }
    ],
    //@ts-ignore
    getMeteringTimeseriesQuery
  );

  const {
    //@ts-ignore
    distancefrompickuptodropoff: deliveryDistanceDropoffNotAvailableData = [],
    //@ts-ignore
    timefromupcomingtodeliveryended: deliveryTimeDropoffNotAvailableData = []
  } = deliveryTimeDistanceDropoffNotAvailableData;

  const deliveryTimeDropoffAvailVsUnavData = [
    ...deliveryTimeDropoffAvailableData.map(meteringTimeseriesItemNameTransform(t('dashboard.whenDropoffIsAvaliable'))),
    ...deliveryTimeDropoffNotAvailableData.map(
      meteringTimeseriesItemNameTransform(t('dashboard.whenDropoffIsNotAvaliable'))
    )
  ];

  const deliveryDistanceDropoffAvailVsUnavData = [
    ...deliveryDistanceDropoffAvailableData.map(
      meteringTimeseriesItemNameTransform(t('dashboard.whenDropoffIsAvaliable'))
    ),
    ...deliveryDistanceDropoffNotAvailableData.map(
      meteringTimeseriesItemNameTransform(t('dashboard.whenDropoffIsNotAvaliable'))
    )
  ];

  useEffect(() => {
    setTimeWhenDropoffAvailableAvg(getTimeStringFromSeconds(getAverageFromArr(deliveryTimeDropoffAvailableData)));
    setTimeWhenDropoffNotAvailableAvg(getTimeStringFromSeconds(getAverageFromArr(deliveryTimeDropoffNotAvailableData)));
    setDistanceWhenDropoffAvailableAvg(
      `${(getAverageFromArr(deliveryDistanceDropoffAvailableData) / 1000).toFixed(1)}`
    );
    setDistanceWhenDropoffNotAvailableAvg(
      `${(getAverageFromArr(deliveryDistanceDropoffNotAvailableData) / 1000).toFixed(1)}`
    );
  }, [deliveryTimeDropoffAvailVsUnavData, deliveryDistanceDropoffAvailVsUnavData]);

  useEffect(() => {
    const daysCnt = dates && dates[1] ? dates[1].diff(dates[0], 'days') : daysCount;
    setTotalCancelled(
      //@ts-ignore
      cancelledByCaptainNumber + cancelledCapNotFoundNumber + cancelledByMerchantNumber + cancelledByCareNumber
    );
    //@ts-ignore
    setTotalCompleted(completedDeliveredNumber);
    setDaysCount(daysCnt);
  }, [
    isCompletedDeliveredFetching,
    isCancelledByMerchantFetching,
    isCancelledCapNotFoundFetching,
    isCancelledByCaptainFetching,
    isCancelledByCareFetching
  ]);

  useEffect(() => {
    const totalOrders = totalCompleted + totalCancelled;
    const completedPercent = totalOrders ? Math.round((totalCompleted / totalOrders) * 100) : 0;
    const cancelledPercent = totalOrders ? Math.round((totalCancelled / totalOrders) * 100) : 0;
    const avgOrders = Math.round(totalOrders / (daysCount + 1));
    setTotalOrders(totalOrders);
    setTotalCompletedPercent(completedPercent);
    setTotalCancelledPercent(cancelledPercent);
    setAvgOrdersPerDay(avgOrders);
    setTimeFormat(timeFormatter(daysCount));
  }, [totalCompleted, totalCancelled, daysCount]);

  const onDateChange = (values: RangePickerValue) => {
    setDates(values);
  };

  const isTotalCompletedCardLoading = isCompletedDeliveredFetching;

  const isTotalCancelledCardLoading =
    isCancelledByMerchantFetching ||
    isCancelledCapNotFoundFetching ||
    isCancelledByCaptainFetching ||
    isCancelledByCareFetching;

  const isPieChartLoading = isCompletedDeliveredFetching || isTotalCancelledCardLoading;

  const isPieChartError =
    isCompletedDeliveredError ||
    isCancelledByCaptainError ||
    isCancelledByMerchantError ||
    isCancelledCapNotFoundError ||
    isCancelledByCareError;

  const { fireEvent } = useAnalytika();

  const isDashboardReady =
    !isCompletedDeliveredFetching &&
    !isCancelledByMerchantFetching &&
    !isCancelledCapNotFoundFetching &&
    !isCancelledByCaptainFetching &&
    !isCancelledByCareFetching &&
    !isBreakdownDeliveriesByDistancesDataFetching &&
    !isDeliveryTimeDistanceDropoffAvailableDataFetching &&
    !isDeliveryTimeDistanceDropoffNotAvailableDataFetching &&
    !isSingleAreaChartsDataFetching;

  useEffect(() => {
    isDashboardReady &&
      fireEvent(
        dashboardPageOpen({
          date_from: basicQueryParams.startDate,
          date_to: basicQueryParams.endDate,
          //@ts-ignore
          completed_deliveries_count: completedDeliveredNumber,
          cancelled_deliveries_count: totalCancelled,
          average_deliveries_per_day_count: avgOrdersPerDay,
          cancelled_deliveries_percentage: totalCancelledPercent,
          //@ts-ignore
          cnf_cancelled_deliveries_count: cancelledCapNotFoundNumber,
          //@ts-ignore
          cancelled_by_merchant_count: cancelledByMerchantNumber,
          //@ts-ignore
          cancelled_by_captain_count: cancelledByCaptainNumber,
          //@ts-ignore
          cancelled_by_care_count: cancelledByCareNumber,
          time_to_complete_delivery: timeFromStartedToEndedAvg,
          time_to_assign_captain: timeToAssignCaptainAvg,
          time_to_reach_merchant: timeToReachMerchantAvg,
          time_between_pickup_dropoff: timeBtwPickupNDropoffAvg,
          captain_waiting_time_at_merchant: timeCaptainWaitingAvg,
          distance_between_assignment_pickup: distanceBtwAssignmentNPickupAvg,
          distance_between_pickup_dropoff: distanceBtwPickupNDropoffAvg,
          delivery_distance_when_dropoff_available: distanceWhenDropoffAvailableAvg,
          delivery_distance_when_dropoff_not_available: distanceWhenDropoffNotAvailableAvg,
          delivery_time_when_dropoff_available: timeWhenDropoffAvailableAvg,
          delivery_time_when_dropoff_not_available: timeWhenDropoffNotAvailableAvg
        })
      );
  }, [isDashboardReady]);

  return (
    <Page>
      <Row gutter={{ md: 12, lg: 12, xl: 24 }}>
        <Col xs={24} md={6} lg={6} xl={6} xxl={6} className={styles.pageTitleRowItem}>
          <Title text={t('dashboard.pageTitle')} />
        </Col>
        <Col xs={0} md={6} lg={6} xl={6} xxl={6} className={styles.pageTitleRowItem} />
        <Col
          xs={24}
          md={24}
          lg={12}
          xl={12}
          xxl={12}
          className={classnames(styles.pageTitleRowItem, styles.rangeInputContainer)}
        >
          <DatePicker.RangePicker
            allowClear={false}
            className={classnames(styles.filterContainer, styles.rangeInput)}
            onChange={onDateChange}
            disabledDate={isDateDisabled}
            defaultValue={[dayjs(initStart), dayjs(initEnd)]}
            format="MMM Do, YY"
          />
        </Col>
      </Row>
      <Row
        className={styles.dashboardRow}
        gutter={[
          { md: 12, lg: 12, xl: 24 },
          { md: 12, lg: 12, xl: 24 }
        ]}
      >
        <Col xs={24} md={12} lg={8} xl={6} xxl={6}>
          <DashboardChartWidget
            isLoading={isPieChartLoading}
            data={[
              {
                type: t('dashboard.completed'),
                value: totalCompletedPercent,
                numValue: totalCompleted,
                color: colorPrimary
              },
              {
                type: t('dashboard.cancelled'),
                value: totalCancelledPercent,
                numValue: totalCancelled,
                color: colorError
              }
            ]}
            chartTitle={t('dashboard.deliveriesBreakdown')}
            total={totalOrders}
            isError={isPieChartError}
          />
        </Col>
        <Col xs={24} md={24} lg={24} xl={18} xxl={18}>
          <Row
            gutter={[
              { md: 12, lg: 12, xl: 24 },
              { md: 24, lg: 24, xl: 24 }
            ]}
          >
            <Col xs={24} md={12} lg={12} xl={10} xxl={8}>
              <DashboardNumberTileWidget
                isLoading={isTotalCompletedCardLoading}
                mainItem={{ value: totalCompleted, title: t('dashboard.totalCompletedDeliveries') }}
                Icon={CheckmarkIcon}
                isError={isCompletedDeliveredError}
              />
            </Col>
            <Col xs={24} md={12} lg={12} xl={10} xxl={8}>
              <DashboardNumberTileWidget
                isLoading={isPieChartLoading}
                isError={isPieChartError}
                mainItem={{ value: avgOrdersPerDay, title: t('dashboard.averageOrdersPerDay') }}
                Icon={AverageOrdersIcon}
              />
            </Col>
          </Row>
          <Row className={styles.dashboardRow}>
            <Col xs={24} md={24} lg={24} xl={24} xxl={24}>
              <DashboardNumberTileWidget
                isLoading={isTotalCancelledCardLoading}
                mainItem={{ value: totalCancelled, title: t('dashboard.totalCancelledDeliveries') }}
                mainSubtitle={`${totalCancelledPercent}% ${t('dashboard.ofAllOrders')}`}
                Icon={CancelIcon}
                sideItems={[
                  //@ts-ignore
                  { value: cancelledCapNotFoundNumber, title: t('dashboard.captainNotFound') },
                  //@ts-ignore
                  { value: cancelledByMerchantNumber, title: t('dashboard.cancelledByMerchant') },
                  //@ts-ignore
                  { value: cancelledByCaptainNumber, title: t('dashboard.cancelledByCaptain') },
                  //@ts-ignore
                  { value: cancelledByCareNumber, title: t('dashboard.cancelledByCare') }
                ]}
                isError={
                  isCancelledByMerchantError ||
                  isCancelledByCaptainError ||
                  isCancelledCapNotFoundError ||
                  isCancelledByCareError
                }
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row className={styles.dashboardRow} gutter={{ md: 12, lg: 12, xl: 24 }}>
        <Col xs={24} md={24} lg={6} xl={6} xxl={6}>
          <DashboardAreaChartWidget
            legend={[{ total: `${timeFromStartedToEndedAvg} ${t('dashboard.mins')}`, color: colorWarning }]}
            isError={isSingleAreaChartsDataError}
            isLoading={isSingleAreaChartsDataFetching}
            chartTitle={t('dashboard.timeToCompleteDelivery')}
            data={timeToCompleteDeliveryData}
            color={colorWarning}
            areaStylerCallback={customAreaStyleSetter}
            xField="timestamp"
            yField="value"
            tooltipText={t('dashboard.hints.timeToCompleteDelivery')}
            timeFormat={timeFormat}
            tooltipFormat={'min'}
          />
        </Col>
        <Col xs={24} md={24} lg={6} xl={6} xxl={6}>
          <DashboardAreaChartWidget
            legend={[{ total: `${timeToAssignCaptainAvg} ${t('dashboard.mins')}`, color: colorWarning }]}
            isError={isSingleAreaChartsDataError}
            isLoading={isSingleAreaChartsDataFetching}
            chartTitle={t('dashboard.timeToAssignCaptain')}
            data={timeToAssignCaptainData}
            color={colorWarning}
            areaStylerCallback={customAreaStyleSetter}
            xField="timestamp"
            yField="value"
            tooltipText={t('dashboard.hints.timeToAssignCaptain')}
            timeFormat={timeFormat}
            tooltipFormat={'min'}
          />
        </Col>
        <Col xs={24} md={24} lg={6} xl={6} xxl={6}>
          <DashboardAreaChartWidget
            legend={[{ total: `${timeToReachMerchantAvg} ${t('dashboard.mins')}`, color: colorWarning }]}
            isError={isSingleAreaChartsDataError}
            isLoading={isSingleAreaChartsDataFetching}
            chartTitle={t('dashboard.timeToReachMerchant')}
            data={timeToReachMerchantData}
            color={colorWarning}
            areaStylerCallback={customAreaStyleSetter}
            xField="timestamp"
            yField="value"
            tooltipText={t('dashboard.hints.timeToReachMerchant')}
            timeFormat={timeFormat}
            tooltipFormat={'min'}
          />
        </Col>
        <Col xs={24} md={24} lg={6} xl={6} xxl={6}>
          <DashboardAreaChartWidget
            legend={[{ total: `${timeCaptainWaitingAvg} ${t('dashboard.mins')}`, color: colorWarning }]}
            isError={isSingleAreaChartsDataError}
            isLoading={isSingleAreaChartsDataFetching}
            chartTitle={t('dashboard.timeCaptainWaiting')}
            data={timeCaptainWaitingData}
            color={colorWarning}
            areaStylerCallback={customAreaStyleSetter}
            xField="timestamp"
            yField="value"
            tooltipText={t('dashboard.hints.timeCaptainWaiting')}
            timeFormat={timeFormat}
            tooltipFormat={'min'}
          />
        </Col>
      </Row>
      <Row className={styles.dashboardRow} gutter={{ md: 12, lg: 12, xl: 24 }}>
        <Col xs={24} md={24} lg={6} xl={6} xxl={6}>
          <DashboardAreaChartWidget
            legend={[{ total: `${timeBtwPickupNDropoffAvg} ${t('dashboard.mins')}`, color: colorWarning }]}
            isError={isSingleAreaChartsDataError}
            isLoading={isSingleAreaChartsDataFetching}
            chartTitle={t('dashboard.timeBtwPickupNDropoff')}
            data={timeBtwPickupNDropoffData}
            color={colorWarning}
            areaStylerCallback={customAreaStyleSetter}
            xField="timestamp"
            yField="value"
            tooltipText={t('dashboard.hints.timeBtwPickupNDropoff')}
            timeFormat={timeFormat}
            tooltipFormat={'min'}
          />
        </Col>
        <Col xs={24} md={24} lg={6} xl={6} xxl={6}>
          <DashboardAreaChartWidget
            legend={[{ total: `${distanceBtwAssignmentNPickupAvg} ${t('dashboard.kms')}`, color: colorWarning }]}
            isError={isSingleAreaChartsDataError}
            isLoading={isSingleAreaChartsDataFetching}
            chartTitle={t('dashboard.distanceBtwAssignmentNPickup')}
            data={distanceBtwAssignmentNPickupData}
            color={colorWarning}
            areaStylerCallback={customAreaStyleSetter}
            xField="timestamp"
            yField="value"
            tooltipText={t('dashboard.hints.distanceBtwAssignmentNPickup')}
            timeFormat={timeFormat}
            tooltipFormat={'km'}
          />
        </Col>
        <Col xs={24} md={24} lg={6} xl={6} xxl={6}>
          <DashboardAreaChartWidget
            legend={[{ total: `${distanceBtwPickupNDropoffAvg} ${t('dashboard.kms')}`, color: colorWarning }]}
            isError={isSingleAreaChartsDataError}
            isLoading={isSingleAreaChartsDataFetching}
            chartTitle={t('dashboard.distanceBtwPickupNDropoff')}
            data={distanceBtwPickupNDropoffData}
            color={colorWarning}
            areaStylerCallback={customAreaStyleSetter}
            xField="timestamp"
            yField="value"
            tooltipText={t('dashboard.hints.distanceBtwPickupNDropoff')}
            timeFormat={timeFormat}
            tooltipFormat={'km'}
          />
        </Col>
        <Col xs={24} md={24} lg={6} xl={6} xxl={6}>
          {showHistogram && (
            <DashboardColumnChartWidget
              chartTotal={totalOrders}
              isError={isBreakdownDeliveriesByDistancesDataError}
              isLoading={isBreakdownDeliveriesByDistancesDataFetching}
              chartTitle={t('dashboard.breakdownDeliveriesByDistances')}
              data={breakdownDeliveriesByDistancesData}
              xField="name"
              yField="value"
              tooltipText={t('dashboard.hints.breakdownDeliveriesByDistances')}
            />
          )}
        </Col>
      </Row>
      <Row className={styles.dashboardRow} gutter={{ md: 12, lg: 12, xl: 24 }}>
        <Col xs={24} md={24} lg={12} xl={12} xxl={12}>
          <DashboardAreaChartWidget
            legend={[
              {
                total: `${timeWhenDropoffAvailableAvg} ${t('dashboard.mins')}`,
                subtitle: t('dashboard.whenDropoffIsAvaliable'),
                color: colorPrimary
              },
              {
                total: `${timeWhenDropoffNotAvailableAvg} ${t('dashboard.mins')}`,
                subtitle: t('dashboard.whenDropoffIsNotAvaliable'),
                color: colorWarning
              }
            ]}
            isError={
              isDeliveryTimeDistanceDropoffAvailableDataError || isDeliveryTimeDistanceDropoffNotAvailableDataError
            }
            isLoading={
              isDeliveryTimeDistanceDropoffAvailableDataFetching ||
              isDeliveryTimeDistanceDropoffNotAvailableDataFetching
            }
            chartTitle={t('dashboard.deliveryTimeDropoffAvailVsUnav')}
            data={deliveryTimeDropoffAvailVsUnavData}
            color={[colorPrimary, colorWarning]}
            areaStylerCallback={customAreaStyleSetter}
            xField="timestamp"
            yField="value"
            seriesField="name"
            tooltipText={t('dashboard.hints.deliveryTimeDropoffAvailVsUnav')}
            useLine
            timeFormat={timeFormat}
            tooltipFormat={'min'}
          />
        </Col>
        <Col xs={24} md={24} lg={12} xl={12} xxl={12}>
          <DashboardAreaChartWidget
            legend={[
              {
                total: `${distanceWhenDropoffAvailableAvg} ${t('dashboard.kms')}`,
                subtitle: t('dashboard.whenDropoffIsAvaliable'),
                color: colorPrimary
              },
              {
                total: `${distanceWhenDropoffNotAvailableAvg} ${t('dashboard.kms')}`,
                subtitle: t('dashboard.whenDropoffIsNotAvaliable'),
                color: colorWarning
              }
            ]}
            isError={
              isDeliveryTimeDistanceDropoffAvailableDataError || isDeliveryTimeDistanceDropoffNotAvailableDataError
            }
            isLoading={
              isDeliveryTimeDistanceDropoffAvailableDataFetching ||
              isDeliveryTimeDistanceDropoffNotAvailableDataFetching
            }
            chartTitle={t('dashboard.deliveryDistanceDropoffAvailVsUnav')}
            data={deliveryDistanceDropoffAvailVsUnavData}
            color={[colorPrimary, colorWarning]}
            areaStylerCallback={customAreaStyleSetter}
            xField="timestamp"
            yField="value"
            seriesField="name"
            tooltipText={t('dashboard.hints.deliveryDistanceDropoffAvailVsUnav')}
            useLine
            timeFormat={timeFormat}
            tooltipFormat={'km'}
          />
        </Col>
      </Row>
    </Page>
  );
};

export default DashboardPage;
